import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import { useQuery } from '@tanstack/react-query';
import { XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
  fairLendingApi,
  type Firm,
  type PartnerReportLetterDefaults,
  type PartnerReportRequest,
  type PartnerReportSection,
} from '../../lib/api';

// Partner-branded client report (CLARID-SPEC-PBR-001): a firm member's white-labeled
// "Fair Lending Data Analyses" Word draft for this bank, built from this FLD analysis.

const MAX_ADDRESS_LINES = 4;
const MAX_CC_LINES = 6; // matches the server's letter schema

const inputClass =
  'block w-full rounded-md border border-rule bg-white px-3 py-2 text-sm text-ink placeholder:text-ink-4 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';
const labelClass = 'block text-sm font-medium text-ink-2 mb-1';

/** "October 28, 2026" — the form the letter prints. */
function formatLetterDate(d: Date): string {
  return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
}

function addDays(d: Date, days: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + days);
  return out;
}

/** The checklist in report order, one group per aspect of credit. */
function groupByAspect(sections: PartnerReportSection[]): [string, PartnerReportSection[]][] {
  const groups = new Map<string, PartnerReportSection[]>();
  for (const s of sections) groups.set(s.aspect, [...(groups.get(s.aspect) ?? []), s]);
  return [...groups];
}

function toLines(text: string): string[] {
  return text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
}

/** With responseType 'blob' an error body arrives as a Blob of JSON; read the server's message out of it. */
async function readBlobError(error: any): Promise<string> {
  const data = error?.response?.data;
  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      if (typeof parsed?.error === 'string') return parsed.error;
    } catch {
      /* not JSON — fall through */
    }
  }
  if (!error?.response) return 'Network error. Check your connection and try again.';
  return 'The client report could not be generated.';
}

function readGaps(headers: any): string[] {
  const raw = headers?.['x-report-gaps'] as string | undefined;
  if (!raw) return [];
  try {
    const parsed = JSON.parse(decodeURIComponent(raw));
    return Array.isArray(parsed) ? parsed.filter((g): g is string => typeof g === 'string' && g.trim() !== '') : [];
  } catch {
    return [];
  }
}

interface LetterForm {
  date: string;
  recipientName: string;
  recipientTitle: string;
  institutionName: string;
  address: string;
  salutation: string;
  responseDueDate: string;
  signerName: string;
  signerTitle: string;
  cc: string;
  reportYear: string;
  draft: boolean;
}

function blankForm(): LetterForm {
  const today = new Date();
  return {
    date: formatLetterDate(today),
    recipientName: '',
    recipientTitle: '',
    institutionName: '',
    address: '',
    salutation: '',
    responseDueDate: formatLetterDate(addDays(today, 30)),
    signerName: '',
    signerTitle: '',
    cc: '',
    reportYear: String(today.getFullYear()),
    draft: true,
  };
}

function withDefaults(form: LetterForm, defaults: PartnerReportLetterDefaults | undefined): LetterForm {
  if (!defaults) return form;
  return {
    ...form,
    recipientName: defaults.recipientName ?? '',
    recipientTitle: defaults.recipientTitle ?? '',
    institutionName: defaults.institutionName ?? '',
    address: (defaults.addressLines ?? []).join('\n'),
    salutation: defaults.salutation ?? '',
    signerName: defaults.signerName ?? '',
    signerTitle: defaults.signerTitle ?? '',
    cc: (defaults.cc ?? []).join('\n'),
  };
}

export default function PartnerReportModal({
  isOpen,
  onClose,
  teamId,
  analysisId,
  firms,
  onSubmit,
}: {
  isOpen: boolean;
  onClose: () => void;
  teamId: string;
  analysisId: string;
  firms: Firm[];
  /**
   * The Clarid Agent's card: instead of downloading, the filled-in request is the approval of the
   * agent's action, which stores the report for sign-off. Returns the gaps still to fill in by hand.
   */
  onSubmit?: (body: PartnerReportRequest) => Promise<{ gaps: string[] }>;
}) {
  const [orgId, setOrgId] = useState(firms[0]?.id ?? '');
  const [form, setForm] = useState<LetterForm>(blankForm);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gaps, setGaps] = useState<string[] | null>(null);
  // The analyses the SOW covers (Michael Wallace, 2026-10-27). Null until the checklist has loaded.
  const [selected, setSelected] = useState<Set<string> | null>(null);

  const firm = firms.find((f) => f.id === orgId) ?? firms[0];

  const { data: sectionData } = useQuery({
    queryKey: ['partner-report-sections', teamId, analysisId, firm?.id],
    queryFn: async () => (await fairLendingApi.partnerReportSections(teamId, analysisId, firm!.id)).data,
    enabled: isOpen && !!firm,
    staleTime: 0,
  });
  const sections: PartnerReportSection[] = sectionData?.sections ?? [];

  // Last time's selection for this firm and bank, else every section; a section new since then starts ticked
  // only when there was no saved selection.
  useEffect(() => {
    if (!isOpen || !sectionData) return;
    const keys = sectionData.sections.map((s) => s.key);
    const saved = sectionData.selected;
    setSelected(new Set(saved ? keys.filter((k) => saved.includes(k)) : keys));
  }, [isOpen, sectionData]);

  const toggleSection = (key: string) =>
    setSelected((cur) => {
      const next = new Set(cur ?? []);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  const { data: defaults, isFetching: loadingDefaults } = useQuery({
    queryKey: ['partner-report-defaults', teamId, analysisId, firm?.id],
    queryFn: async () => (await fairLendingApi.partnerReportDefaults(teamId, analysisId, firm!.id)).data.letter,
    enabled: isOpen && !!firm,
    staleTime: 0,
  });

  // Fresh form each time the modal opens. Declared before the defaults effect so, on open, the blank
  // form is queued first and the defaults are applied on top of it.
  useEffect(() => {
    if (!isOpen) return;
    setForm(blankForm());
    setError(null);
    setGaps(null);
    setSelected(null);
    setOrgId((current) => (firms.some((f) => f.id === current) ? current : firms[0]?.id ?? ''));
    // Only on open: re-running on every new `firms` array would wipe what the user typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Letter fields from the defaults — on open (cached defaults, if any) and whenever they change: a
  // refetch or a different firm. Letter date, due date, report year and Draft are kept.
  useEffect(() => {
    if (!isOpen) return;
    setForm((f) => withDefaults(f, defaults));
  }, [isOpen, defaults]);

  const set = <K extends keyof LetterForm>(key: K, value: LetterForm[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const addressLines = toLines(form.address);
  const ccLines = toLines(form.cc);
  const year = Number(form.reportYear);
  const yearValid = Number.isInteger(year) && year >= 2000 && year <= 2100;
  const formError =
    addressLines.length > MAX_ADDRESS_LINES
      ? `Use at most ${MAX_ADDRESS_LINES} address lines.`
      : ccLines.length > MAX_CC_LINES
        ? `Use at most ${MAX_CC_LINES} cc lines.`
        : !form.date.trim()
          ? 'Enter the letter date.'
          : !yearValid
            ? 'Enter a report year between 2000 and 2100.'
            : selected && sections.length > 0 && selected.size === 0
              ? 'Choose at least one analysis the SOW covers.'
              : null;

  const handleGenerate = async () => {
    if (!firm || generating || formError) return;
    setGenerating(true);
    setError(null);

    const recipientName = form.recipientName.trim();
    const salutation = form.salutation.trim() || (recipientName ? `Dear ${recipientName}:` : '');
    const body: PartnerReportRequest = {
      organizationId: firm.id,
      reportYear: year,
      letter: {
        date: form.date.trim(),
        recipientName,
        recipientTitle: form.recipientTitle.trim(),
        institutionName: form.institutionName.trim(),
        addressLines,
        salutation,
        responseDueDate: form.responseDueDate.trim(),
        signerName: form.signerName.trim(),
        signerTitle: form.signerTitle.trim(),
        cc: ccLines,
        draft: form.draft,
      },
      // In checklist order; omitted until the checklist has loaded, which prints every section.
      ...(selected ? { sections: sections.map((s) => s.key).filter((k) => selected.has(k)) } : {}),
    };

    if (onSubmit) {
      try {
        const { gaps: remaining } = await onSubmit(body);
        if (remaining.length > 0) setGaps(remaining);
        else {
          toast.success('Client report prepared');
          onClose();
        }
      } catch (err: any) {
        setError(err?.response?.data?.error ?? 'The client report could not be prepared.');
      } finally {
        setGenerating(false);
      }
      return;
    }

    try {
      const res = await fairLendingApi.generatePartnerReport(teamId, analysisId, body);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      const cd = (res.headers as any)['content-disposition'] as string | undefined;
      const match = cd?.match(/filename="?([^"]+)"?/);
      a.download = match?.[1] || 'Fair_Lending_Data_Analyses.docx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      const remaining = readGaps(res.headers);
      if (remaining.length > 0) {
        setGaps(remaining);
      } else {
        toast.success('Client report downloaded');
        onClose();
      }
    } catch (err) {
      const message = await readBlobError(err);
      setError(message);
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-2xl bg-panel rounded-xl shadow-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <Dialog.Title className="text-lg font-semibold text-ink">Client report</Dialog.Title>
                  <button onClick={onClose} className="text-ink-4 hover:text-ink-3" aria-label="Close">
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {gaps ? (
                  <>
                    <p className="text-sm text-ink-2">
                      The report has downloaded. A few items could not be filled from the data.
                    </p>
                    <div className="mt-4 rounded-lg border border-rule bg-paper p-4">
                      <h3 className="text-sm font-semibold text-ink">Still to fill in by hand</h3>
                      <ul className="mt-2 list-disc pl-5 space-y-1 text-sm text-ink-2">
                        {gaps.map((g) => (
                          <li key={g}>{g}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={() => setGaps(null)}
                        className="rounded-md px-4 py-2 text-sm font-medium text-ink-2 hover:bg-panel-2"
                      >
                        Back to the form
                      </button>
                      <button
                        onClick={onClose}
                        className="rounded-md px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700"
                      >
                        Done
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="space-y-4">
                      <div className="flex items-end justify-between gap-4">
                        {firms.length > 1 ? (
                          <div className="flex-1">
                            <label htmlFor="pr-firm" className={labelClass}>Firm</label>
                            <select
                              id="pr-firm"
                              value={firm?.id ?? ''}
                              onChange={(e) => setOrgId(e.target.value)}
                              className={inputClass}
                            >
                              {firms.map((f) => (
                                <option key={f.id} value={f.id}>
                                  {f.reportDisplayName || f.name}
                                </option>
                              ))}
                            </select>
                          </div>
                        ) : (
                          <p className="text-sm text-ink-2">
                            Prepared by <span className="font-medium text-ink">{firm?.reportDisplayName || firm?.name}</span>
                          </p>
                        )}
                        {firm?.role === 'ADMIN' && (
                          <Link to="/firm-branding" className="text-sm text-teal-600 hover:underline whitespace-nowrap">
                            Firm branding
                          </Link>
                        )}
                      </div>

                      {firm?.libraryStatus === 'PENDING_MICHAEL_APPROVAL' && (
                        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                          Recommendation wording is pending Michael Wallace&apos;s approval. Review the Observation &amp;
                          Recommendations section before sending.
                        </div>
                      )}

                      <fieldset disabled={generating} className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          <Field id="pr-date" label="Letter date">
                            <input id="pr-date" className={inputClass} value={form.date} onChange={(e) => set('date', e.target.value)} />
                          </Field>
                          <Field id="pr-due" label="Response due date">
                            <input id="pr-due" className={inputClass} value={form.responseDueDate} onChange={(e) => set('responseDueDate', e.target.value)} />
                          </Field>
                          <Field id="pr-rname" label="Recipient name">
                            <input id="pr-rname" className={inputClass} value={form.recipientName} onChange={(e) => set('recipientName', e.target.value)} />
                          </Field>
                          <Field id="pr-rtitle" label="Recipient title">
                            <input id="pr-rtitle" className={inputClass} value={form.recipientTitle} onChange={(e) => set('recipientTitle', e.target.value)} />
                          </Field>
                          <Field id="pr-inst" label="Institution name">
                            <input id="pr-inst" className={inputClass} value={form.institutionName} onChange={(e) => set('institutionName', e.target.value)} />
                          </Field>
                          <Field id="pr-sal" label="Salutation">
                            <input
                              id="pr-sal"
                              className={inputClass}
                              value={form.salutation}
                              placeholder={form.recipientName.trim() ? `Dear ${form.recipientName.trim()}:` : ''}
                              onChange={(e) => set('salutation', e.target.value)}
                            />
                          </Field>
                          <Field id="pr-addr" label="Address" hint={`One line per row, up to ${MAX_ADDRESS_LINES}.`}>
                            <textarea id="pr-addr" rows={4} className={inputClass} value={form.address} onChange={(e) => set('address', e.target.value)} />
                          </Field>
                          <Field id="pr-cc" label="Cc" hint="One person per row.">
                            <textarea id="pr-cc" rows={4} className={inputClass} value={form.cc} onChange={(e) => set('cc', e.target.value)} />
                          </Field>
                          <Field id="pr-sname" label="Signer name">
                            <input id="pr-sname" className={inputClass} value={form.signerName} onChange={(e) => set('signerName', e.target.value)} />
                          </Field>
                          <Field id="pr-stitle" label="Signer title">
                            <input id="pr-stitle" className={inputClass} value={form.signerTitle} onChange={(e) => set('signerTitle', e.target.value)} />
                          </Field>
                          <Field id="pr-year" label="Report year">
                            <input
                              id="pr-year"
                              type="number"
                              min={2000}
                              max={2100}
                              className={inputClass}
                              value={form.reportYear}
                              onChange={(e) => set('reportYear', e.target.value)}
                            />
                          </Field>
                          <div className="flex items-end pb-2">
                            <label className="inline-flex items-center gap-2 text-sm text-ink-2">
                              <input
                                type="checkbox"
                                checked={form.draft}
                                onChange={(e) => set('draft', e.target.checked)}
                                className="h-4 w-4 rounded border-rule text-teal-600 focus:ring-teal-500"
                              />
                              Draft
                            </label>
                          </div>
                        </div>

                        {sections.length > 0 && selected && (
                          <div className="rounded-lg border border-rule p-3">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-sm font-medium text-ink">Analyses in the SOW</p>
                              <div className="flex gap-3 text-xs">
                                <button type="button" className="text-teal-600 hover:underline" onClick={() => setSelected(new Set(sections.map((s) => s.key)))}>
                                  All
                                </button>
                                <button type="button" className="text-teal-600 hover:underline" onClick={() => setSelected(new Set())}>
                                  None
                                </button>
                              </div>
                            </div>
                            <p className="mt-1 text-xs text-ink-3">
                              Unticked analyses are left out of the report: their section, Scope line, Summary of Risks rows and
                              Focal Point or Targeted Review rows. The Scope, institution, lending area and HMDA Data Summary always print.
                            </p>
                            <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
                              {groupByAspect(sections).map(([aspect, items]) => (
                                <div key={aspect}>
                                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-3">{aspect}</p>
                                  {items.map((s) => (
                                    <label key={s.key} className="mt-1 flex items-start gap-2 text-sm text-ink-2">
                                      <input
                                        type="checkbox"
                                        checked={selected.has(s.key)}
                                        onChange={() => toggleSection(s.key)}
                                        className="mt-0.5 h-4 w-4 rounded border-rule text-teal-600 focus:ring-teal-500"
                                      />
                                      {s.label}
                                    </label>
                                  ))}
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </fieldset>

                      {loadingDefaults && <p className="text-xs text-ink-4">Loading saved letter details…</p>}
                      {(formError || error) && (
                        <p className="text-sm text-red-600" role="alert">
                          {formError || error}
                        </p>
                      )}
                    </div>

                    <div className="mt-6 flex justify-end gap-3">
                      <button
                        onClick={onClose}
                        className="rounded-md px-4 py-2 text-sm font-medium text-ink-2 hover:bg-panel-2"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleGenerate}
                        disabled={!firm || generating || !!formError}
                        className="rounded-md px-4 py-2 text-sm font-medium bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {generating ? 'Generating…' : 'Generate'}
                      </button>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

function Field({ id, label, hint, children }: { id: string; label: string; hint?: string; children: ReactNode }) {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>
        {label}
      </label>
      {children}
      {hint && <p className="mt-1 text-xs text-ink-4">{hint}</p>}
    </div>
  );
}
